import { sanitizeChatMessage } from '../../utils/safeHtml';
import {
  porAtendente,
  porDepartamento,
  totalAtivoAgora,
} from '../../utils/atendimentosAtivosAgora';
import { chatAttachmentUrl } from '../../utils/chatAttachment';
import React, { useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
  Menu,
  InputAdornment,
} from '@mui/material';
import {
  HeadsetMic,
  WhatsApp,
  ChatBubbleOutline,
  AccessTime,
  Refresh,
  Visibility,
  Login,
  Close,
  InsertDriveFileOutlined,
  Search,
  SwapHoriz,
  CheckCircle,
  RemoveCircleOutline,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import http from '../../services/http';
import { ThemeContext } from '../../components/ThemeContext/ThemeContext';
import { DATE_PERIOD_LABELS, resolveDatePeriodRange } from '../../utils/datePeriods';
import { hasAdminProfile } from '../../utils/profileRole';
import { ordenarPor, proximaOrdem, rotuloDaOrdenacao, CRESCENTE } from '../../utils/ordenacaoDaTabela';
// O predicado UNICO de "esta sala e de WhatsApp?". O painel decidia isso
// sozinho, por `whatsapp_instance_id` -- coluna morta desde 31/08 --, e por
// isso desenhava toda conversa de WhatsApp com o icone do chat do site.
import { ehSalaDeWhatsapp } from '../../utils/janelaDoWhatsapp';
// A marca (SysFar, MAX) por onde o cliente chegou. A guarda de canal mora no
// util: aqui basta chamar, sem repetir a condicao de WhatsApp.
import { marcaDaSala } from '../../utils/marcaDaSala';
// O vocabulario do filtro de canal (item 8) mora num util desde que o item 6
// pediu o MESMO filtro na lista lateral do chat ao vivo. Duas copias das
// mesmas tres constantes e do mesmo predicado e exatamente a duplicacao que
// produziu o defeito de 29/09, com toda conversa de WhatsApp aparecendo
// neste painel como chat do site.
import { CANAIS, CANAL_TODOS, passaNoCanal } from '../../utils/canalDaConversa';
import { separarLeads } from '../../utils/leadsDoPainel';
import { telefoneLegivel } from '../../utils/telefoneDaSala';
import { previaDaConversa } from '../../utils/anexoDaMensagem';
import {
  TODAS_AS_ORIGENS,
  filtrarConversasDoPainel,
  filtrarAtendimentosDoPainel,
  conversasEmLinha,
  origensDasConversas,
  CHAT_DO_SITE,
  ROTULO_DO_CHAT_DO_SITE,
  TODA_PRESENCA,
  PRESENCAS,
  filtrarAgentesDoPainel,
  statusEhOnline,
  departamentosDosAgentes,
  rotulosDosDepartamentosDoPainel,
} from '../../utils/filtrosDoPainel';

// Períodos oferecidos no painel (subconjunto, na ordem do Movidesk).
const PANEL_PERIODS = [
  'today', 'yesterday', 'specific_day',
  'this_week', 'last_week', 'specific_week',
  'this_month', 'previous_month', 'specific_month',
];

const POLL_INTERVAL_MS = 8000;
const VIEWER_POLL_MS = 5000;

// Tempo decorrido desde o início do atendimento (ex: 12min, 1h03).
const elapsedSince = (isoDate) => {
  if (!isoDate) return '';
  const diffMs = Date.now() - new Date(isoDate).getTime();
  if (diffMs < 0) return '0min';
  const totalMin = Math.floor(diffMs / 60000);
  if (totalMin < 60) return `${totalMin}min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}h${String(m).padStart(2, '0')}`;
};

/**
 * Data e hora curtas, para a fila (ponto 12 da Gisele, 20/10/2026).
 *
 * "Hoje" sai só com a hora: numa fila, o dia de hoje é o caso comum e repetir
 * a data em toda linha gasta espaço sem informar nada. Quando a conversa
 * atravessou a meia-noite -- e isso acontece -- a data aparece, porque aí ela é
 * exatamente a informação que muda a decisão.
 */
const dataHoraCurta = (isoDate) => {
  if (!isoDate) return '';

  const d = new Date(isoDate);

  if (Number.isNaN(d.getTime())) return '';

  const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const ehHoje = d.toDateString() === new Date().toDateString();

  return ehHoje ? hora : `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${hora}`;
};

// Modal de visualização somente leitura da conversa.
/**
 * A conversa em SOMENTE LEITURA.
 *
 * Nasceu para o cartão "em atendimento" -- ver antes de entrar na conversa de
 * outro técnico. Em 22/10 o dono do produto pediu o mesmo botão nas outras duas
 * tabelas (Leads e fila): "uma miniatura do nosso design de chat, somente para
 * ver as mensagens trocadas".
 *
 * ## Por que a fila NÃO ganha o botão de entrar aqui dentro
 *
 * `podeEntrar` nasce verdadeiro para não mudar o cartão de atendimento, onde
 * "Entrar no atendimento" sempre esteve. Mas na fila e nos leads a própria
 * linha já tem "Pegar" e "Entrar", a um clique de distância -- repetir a ação
 * dentro do modal criaria um segundo caminho para assumir a conversa, com
 * outra confirmação e outro estado de carregamento. O pedido ali era ver, e
 * ver é o que ele faz.
 */
const ChatViewerDialog = ({ open, chat, onClose, onJoin, theme, podeEntrar = true }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    if (!chat?.room_id) return;
    try {
      const res = await http.get(`/messages/${chat.room_id}`, { params: { limit: 100 } });
      const data = Array.isArray(res.data?.data) ? res.data.data : (Array.isArray(res.data) ? res.data : []);
      // Endpoint retorna em ordem decrescente; exibimos em ordem cronológica.
      setMessages([...data].reverse());
    } catch (err) {
      console.error('Erro ao carregar conversa:', err);
    } finally {
      setLoading(false);
    }
  }, [chat]);

  useEffect(() => {
    if (!open) return undefined;
    setLoading(true);
    fetchMessages();
    const id = setInterval(fetchMessages, VIEWER_POLL_MS);
    return () => clearInterval(id);
  }, [open, fetchMessages]);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView();
  }, [messages]);

  const renderBody = (msg) => {
    if (msg.archive) {
      const isImage = /\.(jpeg|jpg|gif|png|webp)$/i.test(msg.archive) || msg.archive.startsWith('data:image');
      if (isImage) {
        const src = msg.archive.startsWith('http') || msg.archive.startsWith('data:')
          ? msg.archive
          : chatAttachmentUrl(chat.room_id, msg.archive);
        return <Box component="img" src={src} alt="" sx={{ maxWidth: '12rem', maxHeight: '12rem', borderRadius: 1, display: 'block' }} />;
      }
      const isAudio = /\.(ogg|opus|mp3|wav)$/i.test(msg.archive);
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <InsertDriveFileOutlined fontSize="small" />
          <Typography variant="body2">{isAudio ? 'Áudio' : (msg.message || 'Arquivo')}</Typography>
        </Box>
      );
    }
    return <span dangerouslySetInnerHTML={{ __html: sanitizeChatMessage(msg.message) }} />;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{chat?.client_name || 'Conversa'}</Typography>
          <Typography variant="caption" color="text.secondary">Somente leitura</Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: 360, maxHeight: 480, backgroundColor: theme.palette.background.default }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
        ) : messages.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>Sem mensagens.</Typography>
        ) : (
          messages.map((msg, i) => {
            const isOperator = !!msg.type && msg.type !== 0;
            const isSystem = msg.type === 4;
            if (isSystem) {
              return (
                <Box key={i} sx={{ textAlign: 'center', my: 1 }}>
                  {/*
                    As automaticas (boas-vindas, encerramento) sao gravadas como
                    HTML, igual as demais -- este ramo por TIPO era o que ainda as
                    escapava, entao as tags <p> apareciam literais.
                  */}
                  <Typography variant="caption" component="div" sx={{ color: theme.palette.text.disabled }} dangerouslySetInnerHTML={{ __html: sanitizeChatMessage(msg.message) }} />
                </Box>
              );
            }
            return (
              <Box key={i} sx={{ display: 'flex', justifyContent: isOperator ? 'flex-end' : 'flex-start', mb: 1 }}>
                <Box sx={{
                  maxWidth: '75%',
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  backgroundColor: isOperator ? theme.palette.background.messageBox : theme.palette.background.received,
                  color: theme.palette.text.primary,
                }}>
                  {msg.user?.name && (
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.25 }}>{msg.user.name}</Typography>
                  )}
                  {renderBody(msg)}
                </Box>
              </Box>
            );
          })
        )}
        <div ref={endRef} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Fechar</Button>
        {podeEntrar && (
          <Button onClick={() => onJoin(chat)} variant="contained" startIcon={<Login />}>Entrar no atendimento</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// Formata uma duração em segundos para exibição (ex.: 45s, 12min, 1h05).
const formatDuration = (sec) => {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}min`;
  const h = Math.floor(m / 60);
  return `${h}h${String(m % 60).padStart(2, '0')}`;
};

/**
 * Cartão de seção do painel (título + conteúdo) no nosso design.
 *
 * ## `flexShrink: 0` não é detalhe: é o conserto do corte
 *
 * Em tela baixa, as conversas apareciam cortadas no meio da linha -- "Sem
 * conversas aguardando." saía pela metade -- e rolar a página não fazia nada.
 *
 * Medido no navegador: o corpo do cartão tinha `clientHeight 72` para
 * `scrollHeight 95`. Os cartões são itens de um flex em coluna, e o padrão
 * `flex-shrink: 1` deixa o flex ENCOLHÊ-LOS abaixo do conteúdo. O
 * `overflow: hidden` daqui -- que existe para o conteúdo respeitar o
 * arredondamento da borda -- então cortava o que sobrava.
 *
 * E isso explicava o pior sintoma: como nenhum cartão transbordava, o
 * container de rolagem media `553/553` e **não criava barra**. A página não
 * rolava porque, para o navegador, não havia nada além da dobra -- o conteúdo
 * tinha sido cortado antes.
 *
 * Com `flexShrink: 0` o cartão mantém a altura do conteúdo, o container passa
 * a `553/600` e rola. Verificado ao vivo antes de escrever esta linha.
 */
/**
 * `acao` é um canto à direita do título, para o cartão carregar o próprio
 * controle. Nasceu do filtro por cartão (14/10): com os controles todos na
 * barra do topo, nada dizia QUAL lista cada um recortava.
 */
/**
 * Empresa e telefone, nessa ordem, sem linha vazia quando falta um (ponto 12).
 *
 * Nem toda conversa tem empresa (o lead não se apresentou) e nem toda tem
 * telefone (o chat do site não tem número). Renderizar o separador sempre
 * deixaria linhas começando ou terminando em "·".
 */
const LinhaDeIdentificacao = ({ empresa, telefone, theme }) => {
  const partes = [empresa, telefone].filter((p) => String(p || '').trim() !== '');

  if (partes.length === 0) return null;

  return (
    <Typography variant="caption" noWrap sx={{ color: theme.palette.text.secondary, display: 'block' }}>
      {partes.join(' · ')}
    </Typography>
  );
};

/**
 * A prévia da última mensagem da conversa (ponto 12).
 *
 * Quem olha a fila está decidindo o que atender primeiro, e o nome do cliente
 * não diz se a pessoa mandou um "bom dia" ou um erro de emissão de nota.
 *
 * Não renderiza nada quando a conversa ainda não tem mensagem -- a sala entra
 * na fila no instante em que é criada, e a primeira mensagem pode chegar
 * depois. Uma linha em branco ali faria a altura da fila pular sozinha.
 */
const PreviaDaFila = ({ room, theme }) => {
  const previa = previaDaConversa(room?.ultima_mensagem);

  if (!previa) return null;

  return (
    <Typography
      variant="caption"
      noWrap
      data-testid="previa-da-conversa"
      sx={{ color: theme.palette.text.disabled, display: 'block', fontStyle: 'italic' }}
    >
      {previa}
    </Typography>
  );
};

/** Onde fica a escolha de recolher cada bloco, por operador. */
const CHAVE_DOS_BLOCOS_RECOLHIDOS = 'alodesk:painel:blocosRecolhidos';

const lerBlocosRecolhidos = () => {
  try {
    const bruto = localStorage.getItem(CHAVE_DOS_BLOCOS_RECOLHIDOS);
    const lido = bruto ? JSON.parse(bruto) : {};
    return lido && typeof lido === 'object' ? lido : {};
  } catch (e) {
    // Vale a mesma regra do resto da tela: preferência ilegível volta ao
    // padrão (tudo aberto), nunca derruba o painel.
    return {};
  }
};

/**
 * Um bloco do painel, que dá para RECOLHER (ponto 10 da Gisele, 20/10/2026).
 *
 * "Poder minimizar ou recolher os blocos Leads, Aguardando e Em atendimento,
 * para quando houver muitas conversas." Em dia cheio os três somados passam da
 * altura da tela, e quem só acompanha a fila rola o tempo todo para não perder
 * o topo de vista.
 *
 * ## O cabeçalho continua visível, e a CONTA junto
 *
 * Recolhido não é escondido: o título traz a contagem, então o bloco fechado
 * ainda responde "tem alguém esperando?". Um bloco que some levaria a resposta
 * junto, e aí ninguém o fecharia.
 *
 * ## A escolha fica no navegador, por operador
 *
 * É preferência de quem está olhando, e muda conforme o dia -- não é
 * configuração da empresa. `localStorage` pela mesma razão que o estado
 * expandido do indicador e da barra lateral moram lá.
 *
 * `recolhivel` é opção: cartões sem `id` (os de dentro de outras telas)
 * continuam como sempre foram.
 */
/**
 * O olho que abre a conversa em somente leitura (22/10/2026).
 *
 * Pedido do dono do produto: o botão que o cartão "em atendimento" já tinha,
 * agora também nos Leads e na fila. Ver antes de pegar muda a decisão -- a
 * prévia diz a última mensagem, não o que já foi conversado.
 *
 * Ícone e não botão com texto: a linha da fila já carrega "Pegar",
 * "Transferir" e "Finalizar", e um quarto rótulo a deixaria ilegível.
 */
const OlhoDaConversa = ({ room, onVer }) => (
  <Tooltip title="Visualizar a conversa" arrow>
    <IconButton
      size="small"
      aria-label={`Visualizar a conversa de ${room.client?.name || 'cliente'}`}
      data-testid={`ver-conversa-${room.room_id}`}
      onClick={() => onVer(room)}
      sx={{ flexShrink: 0 }}
    >
      <Visibility sx={{ fontSize: 17 }} />
    </IconButton>
  </Tooltip>
);

/**
 * Uma das duas colunas de "Atendimentos ativos agora" (ponto 16 da Gisele).
 *
 * As duas sao a mesma coisa com rotulos diferentes -- e por isso um
 * componente, e nao dois blocos de JSX quase iguais: dois blocos sao dois
 * lugares onde o numero pode passar a ser mostrado de um jeito diferente.
 *
 * Linha nenhuma significa "ninguem atendendo neste recorte", e e dito com
 * palavras: uma coluna em branco parece carregamento travado.
 */
const ColunaDeAtivos = ({ titulo, linhas, testid, theme }) => (
  <Box sx={{ flex: 1, minWidth: 0 }} data-testid={testid}>
    <Typography
      variant="caption"
      sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: theme.palette.text.secondary, mb: 1 }}
    >
      {titulo}
    </Typography>
    {linhas.length === 0 ? (
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Ninguem atendendo agora.</Typography>
    ) : (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {linhas.map((linha) => (
          <Box
            key={linha.chave}
            sx={{ display: 'flex', alignItems: 'baseline', gap: 1, justifyContent: 'space-between' }}
          >
            <Typography variant="body2" sx={{ color: theme.palette.text.primary, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {linha.rotulo}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.text.primary, flexShrink: 0 }}>
              {linha.quantidade}
            </Typography>
          </Box>
        ))}
      </Box>
    )}
  </Box>
);
const PanelCard = ({ title, theme, children, acao, id = null, recolhidos = {}, onAlternar = null }) => {
  const recolhivel = Boolean(id && onAlternar);
  const recolhido = recolhivel && Boolean(recolhidos[id]);

  return (
    <Box sx={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, borderRadius: 2, overflow: 'hidden', flexShrink: 0 }}>
      <Box sx={{ px: 2, py: 1.25, backgroundColor: theme.palette.action.hover, borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {recolhivel && (
          <Tooltip title={recolhido ? 'Expandir' : 'Recolher'} arrow>
            <IconButton
              size="small"
              aria-label={`${recolhido ? 'Expandir' : 'Recolher'} ${title}`}
              data-testid={`recolher-${id}`}
              onClick={() => onAlternar(id)}
              sx={{ p: 0.25 }}
            >
              {recolhido ? <ExpandMore sx={{ fontSize: 18 }} /> : <ExpandLess sx={{ fontSize: 18 }} />}
            </IconButton>
          </Tooltip>
        )}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.text.primary, flex: 1, minWidth: 0 }}>{title}</Typography>
        {/* Os filtros do cartão somem com ele: filtrar o que não se vê é
            promessa de resultado invisível. */}
        {!recolhido && acao}
      </Box>
      {!recolhido && children}
    </Box>
  );
};

/**
 * A caixa de busca de UM cartão.
 *
 * Pedido do dono do produto em 14/10: filtro em cada tabela de conversa, e não
 * um só na barra do topo recortando as duas.
 *
 * Mora no cabeçalho do próprio cartão porque é isso que responde "o que esta
 * caixa recorta?" sem ninguém explicar. A caixa anterior ficava na barra de
 * cima, junto de técnico, canal e período, e recortava as duas listas ao mesmo
 * tempo -- quem digitava um nome via as duas mudarem e não sabia qual delas
 * tinha pedido.
 */
/**
 * O seletor de ORIGEM de UM cartão: as instâncias de WhatsApp (MAX, SysFar) e
 * o **chat do site**.
 *
 * Decisão do dono do produto em 14/10. O chat do site entra ao lado das
 * instâncias porque é a mesma pergunta -- "por onde esta pessoa chegou?" --, e
 * um seletor que lista MAX e SysFar sem listar o site deixa a maior parte do
 * tráfego sem resposta.
 *
 * Só aparece com MAIS DE UMA origem naquele cartão: um seletor de uma opção
 * ocupa espaço no cabeçalho e não recorta nada. Na prática ele agora aparece
 * quase sempre, porque basta ter chat do site e uma instância -- antes, quando
 * só listava marcas, o tenant de um número só nunca o via.
 *
 * `testid` por cartão porque os dois convivem na mesma tela, e um teste que
 * pegasse "o seletor de origem" pegaria o primeiro que achasse.
 */
const OrigemDoCartao = ({ valor, onMudar, origens, rotulo, testid, theme }) => {
  if (origens.length < 2) return null;

  return (
    <Select
      size="small"
      value={valor}
      onChange={(e) => onMudar(e.target.value)}
      inputProps={{ 'aria-label': rotulo }}
      SelectDisplayProps={{ 'data-testid': testid }}
      sx={{
        minWidth: 118,
        flexShrink: 0,
        fontSize: '0.8rem',
        // Mesma altura da caixa de busca ao lado: duas alturas no mesmo
        // cabeçalho fazem a linha parecer desalinhada mesmo estando centrada.
        '& .MuiSelect-select': { py: 0, height: 30, display: 'flex', alignItems: 'center' },
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <MenuItem value={TODAS_AS_ORIGENS} sx={{ fontSize: '0.8rem' }}>Todas as origens</MenuItem>
      {origens.map((o) => (
        <MenuItem key={o} value={o} sx={{ fontSize: '0.8rem' }}>
          {o === CHAT_DO_SITE ? ROTULO_DO_CHAT_DO_SITE : o}
        </MenuItem>
      ))}
    </Select>
  );
};

const FiltroDoCartao = ({ valor, onMudar, placeholder, rotulo, theme }) => (
  <TextField
    size="small"
    variant="outlined"
    placeholder={placeholder}
    value={valor}
    onChange={(e) => onMudar(e.target.value)}
    inputProps={{ 'aria-label': rotulo }}
    sx={{
      width: 215,
      flexShrink: 0,
      // Compacto de propósito: ela divide a linha com o título do cartão, e
      // uma caixa de altura normal faria o cabeçalho crescer e empurrar a
      // lista para baixo da dobra -- que foi o defeito de 06/10.
      '& .MuiInputBase-root': { height: 30, fontSize: '0.8rem', backgroundColor: theme.palette.background.paper },
    }}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <Search sx={{ fontSize: 16, color: theme.palette.text.disabled }} />
        </InputAdornment>
      ),
      endAdornment: valor ? (
        <InputAdornment position="end">
          <IconButton size="small" onClick={() => onMudar('')} sx={{ p: 0.25 }} aria-label={`Limpar ${rotulo}`}>
            <Close sx={{ fontSize: 15 }} />
          </IconButton>
        </InputAdornment>
      ) : null,
    }}
  />
);

// Pontinho colorido de status (sistema/chat), com tooltip do rótulo.
/**
 * Um cabeçalho de coluna que ordena ao clique.
 *
 * Pedido do dono do produto em 14/10. É um `button` de verdade, e não um
 * `Typography` com `onClick`: o cabeçalho passa a FAZER algo, e quem navega
 * por teclado ou leitor de tela precisa alcançá-lo e saber o que ele faz --
 * daí o `aria-label` dizendo a próxima ação, não o estado atual.
 *
 * A seta aparece só na coluna ordenada. Mostrar seta apagada em todas
 * transformaria o cabeçalho num campo de ícones onde nenhum chama atenção.
 */
const CabecalhoOrdenavel = ({ titulo, chave, ordem, onOrdenar, largura, alinhamento = 'left', dica }) => {
  const ativa = ordem?.chave === chave;
  const seta = ativa ? (ordem.direcao === CRESCENTE ? '↑' : '↓') : '';
  const justificar = alinhamento === 'right' ? 'flex-end' : (alinhamento === 'center' ? 'center' : 'flex-start');

  const botao = (
    <Box
      component="button"
      type="button"
      onClick={() => onOrdenar(chave)}
      aria-label={rotuloDaOrdenacao(titulo, ordem, chave)}
      sx={{
        all: 'unset',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: justificar,
        gap: 0.25,
        width: largura || 'auto',
        flex: largura ? 'none' : 1,
        fontFamily: 'inherit',
        fontSize: '0.75rem',
        fontWeight: 700,
        lineHeight: 1.66,
        // A coluna ordenada ganha a cor de texto cheia; as outras ficam na
        // secundária. É o mesmo sinal da seta, para quem não distingue a seta
        // de 10px.
        color: (t) => (ativa ? t.palette.text.primary : t.palette.text.secondary),
        '&:hover': { color: (t) => t.palette.text.primary },
        '&:focus-visible': { outline: '2px solid', outlineColor: (t) => t.palette.primary.main, outlineOffset: 2 },
      }}
    >
      <span>{titulo}</span>
      {seta && <span aria-hidden="true">{seta}</span>}
    </Box>
  );

  return dica ? <Tooltip title={dica} arrow>{botao}</Tooltip> : botao;
};

/**
 * O status do operador, em ícone.
 *
 * ## Por que ícone, e não a bolinha que estava aqui
 *
 * Pedido da Gisele (3º bloco). A bolinha distinguia Online de Offline e
 * Disponível de Indisponível **só pela cor** -- verde contra o cinza de
 * `text.disabled`, num círculo de 11px. Duas coisas quebravam:
 *
 * - quem não separa verde de cinza via duas bolinhas idênticas, e a coluna
 *   inteira não dizia nada;
 * - no tema escuro o cinza de texto desabilitado quase desaparece contra a
 *   linha, então "Offline" parecia célula vazia -- e célula vazia se lê como
 *   dado que não carregou, não como estado.
 *
 * O ícone põe o estado na FORMA: preenchido com o traço de confirmação contra
 * vazado com o traço de bloqueio. A cor fica como reforço, não como única
 * portadora da informação. O tooltip (que já existia) continua dizendo a
 * palavra, para quem passa o mouse.
 */
const StatusDoOperador = ({ info, title, onClick }) => {
  const Icone = info.ativo ? CheckCircle : RemoveCircleOutline;

  return (
    <Tooltip title={title} arrow>
      <Icone
        onClick={onClick}
        sx={{
          fontSize: 16,
          color: info.color,
          display: 'block',
          ...(onClick ? {
            cursor: 'pointer',
            transition: 'transform 0.15s',
            '&:hover': { transform: 'scale(1.2)' },
          } : null),
        }}
      />
    </Tooltip>
  );
};

const AttendancePanelPage = () => {
  const { theme } = useContext(ThemeContext);
  const history = useHistory();
  const user = JSON.parse(localStorage.getItem('user'));
  const [agents, setAgents] = useState([]);          // acompanhamento de agentes
  const [indicators, setIndicators] = useState([]);  // indicadores do chat
  const [groups, setGroups] = useState([]);          // conversas EM atendimento (por técnico)
  const [waiting, setWaiting] = useState([]);         // conversas AGUARDANDO
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedChat, setSelectedChat] = useState(null); // diálogo de escolha
  const [viewerChat, setViewerChat] = useState(null); // modal de visualização

  /**
   * Abre a conversa de uma sala da FILA ou dos leads em somente leitura.
   *
   * O modal lê `client_name`, que é o formato do payload de "em atendimento";
   * a sala da fila traz `client.name`. A tradução mora aqui, num lugar só, em
   * vez de o modal aprender dois formatos -- ele já funciona, e dois formatos
   * é como um deles passa a ser esquecido.
   */
  const verConversaDaFila = useCallback((room) => {
    setViewerChat({
      ...room,
      client_name: room.client?.name || telefoneLegivel(room.whatsapp_user_number) || 'Conversa',
      somenteLeitura: true,
    });
  }, []);
  const [pickingId, setPickingId] = useState(null);   // room sendo "pego" da fila

  // Quais blocos estão recolhidos (ponto 10). Lido do navegador na montagem,
  // para a escolha sobreviver ao recarregar -- que é o que torna a opção útil
  // em vez de um clique a cada visita.
  const [blocosRecolhidos, setBlocosRecolhidos] = useState(lerBlocosRecolhidos);

  const alternarBloco = useCallback((id) => {
    setBlocosRecolhidos((atuais) => {
      const proximos = { ...atuais };

      if (proximos[id]) {
        // Sai do mapa em vez de virar `false`: guardar os fechados é o que faz
        // um bloco NOVO nascer aberto sem ninguém lembrar de tratá-lo.
        delete proximos[id];
      } else {
        proximos[id] = true;
      }

      try {
        localStorage.setItem(CHAVE_DOS_BLOCOS_RECOLHIDOS, JSON.stringify(proximos));
      } catch (e) {
        // Navegador sem armazenamento: recolher continua funcionando nesta
        // sessão, só não é lembrado. Não vale avisar ninguém disso.
      }

      return proximos;
    });
  }, []);
  // Período dos INDICADORES e da Qtd. Atend. (não afeta fila/em atendimento, que são "agora").
  const [period, setPeriod] = useState({ operator: 'this_month', value: '' });
  // Canal dos dados (item 8). Recorta a tela INTEIRA: as duas listas, os
  // chips do topo e os contadores por tecnico.
  const [canal, setCanal] = useState(CANAL_TODOS);

  const periodRange = useMemo(() => {
    const r = resolveDatePeriodRange(period.operator, period.value);
    if (!r) return null; // período específico ainda sem valor -> backend usa o padrão (mês atual)
    return { from: new Date(r.startMs).toISOString(), to: new Date(r.endMs).toISOString() };
  }, [period]);

  const fetchDashboard = useCallback(async () => {
    try {
      const periodParams = periodRange ? { from: periodRange.from, to: periodRange.to } : {};
      // So os dois endpoints que CONTAM precisam do canal: a fila e o
      // active-dashboard ja devolvem `is_whatsapp` em cada linha, e sao
      // recortados aqui no navegador.
      const contagemParams = { ...periodParams, canal };
      const [agentsRes, activeRes, waitingRes, indicatorsRes] = await Promise.all([
        http.get('/rooms/agents-panel', { params: contagemParams }),
        http.get('/rooms/active-dashboard'),
        http.get('/rooms/waiting'),
        http.get('/rooms/chat-indicators', { params: contagemParams }),
      ]);
      setAgents(Array.isArray(agentsRes.data) ? agentsRes.data : []);
      setGroups(Array.isArray(activeRes.data) ? activeRes.data : []);
      setWaiting(Array.isArray(waitingRes.data) ? waitingRes.data : []);
      setIndicators(Array.isArray(indicatorsRes.data) ? indicatorsRes.data : []);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Erro ao carregar painel de atendimentos:', err);
    } finally {
      setLoading(false);
    }
  }, [periodRange, canal]);

  useEffect(() => {
    fetchDashboard();
    const intervalId = setInterval(fetchDashboard, POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [fetchDashboard]);

  // A fila e as conversas em atendimento sao recortadas AQUI, nao na api:
  // as duas ja chegam com `is_whatsapp` em cada linha, entao filtrar no
  // navegador responde na hora e -- o que importa mais -- mantem a lista e
  // o numero ao lado dela saindo da MESMA fonte.
  /*
   * Declarados AQUI, acima de quem os lê.
   *
   * Os `useMemo` abaixo rodam durante o render, e `const` não pode ser lido
   * acima da própria linha (zona morta temporal). Declarar junto dos outros
   * estados, mais abaixo, derrubaria a tela com `ReferenceError` -- e é um
   * erro que esta base já pagou uma vez, no ouvinte de Escape do chat.
   */
  /*
   * Uma busca por CARTÃO (pedido do dono do produto em 14/10).
   *
   * Eram uma só, na barra do topo, recortando as duas listas ao mesmo tempo.
   * Quem digitava um nome via as duas mudarem e não sabia qual tinha pedido --
   * e procurar um cliente na fila esvaziava "em atendimento" de lambuja.
   *
   * Duas caixas, cada uma no cabeçalho do seu cartão: a pergunta "o que esta
   * caixa recorta?" passa a se responder sozinha.
   */
  const [buscaNaFila, setBuscaNaFila] = useState('');
  const [buscaEmAtendimento, setBuscaEmAtendimento] = useState('');
  // A marca (MAX, SysFar) também é por cartão, pelo mesmo motivo da busca.
  const [origemNaFila, setOrigemNaFila] = useState(TODAS_AS_ORIGENS);
  const [origemEmAtendimento, setOrigemEmAtendimento] = useState(TODAS_AS_ORIGENS);
  /*
   * Os LEADS ganharam caixa propria (Gisele, 23/10/2026): "incluir filtros
   * para localizar: nome do cliente; telefone; MAX ou Sysfar".
   *
   * A REGRA ja existia -- os leads sempre passaram por
   * filtrarConversasDoPainel, e a busca ja casa nome, telefone (por
   * digitos) e registro. O que faltava era o controle: eles obedeciam a
   * caixa DA FILA, em silencio. Quem digitava um nome na fila via o cartao
   * de Leads encolher sem ter pedido, que e exatamente o defeito que
   * separou as caixas da fila e do em-atendimento em 14/10.
   */
  const [buscaNosLeads, setBuscaNosLeads] = useState('');
  const [origemNosLeads, setOrigemNosLeads] = useState(TODAS_AS_ORIGENS);

  /*
   * Os LEADS saem da fila e vão para o cartão próprio (pedido da Gisele,
   * 19/10/2026): conversa de WhatsApp que recebeu o menu de departamentos e
   * não escolheu nenhum. A regra está em `utils/leadsDoPainel`, com teste.
   *
   * A separação acontece ANTES do filtro de canal e da busca, e as duas
   * listas passam pelos mesmos filtros depois: assim o seletor de canal e a
   * caixa de busca continuam valendo para os dois cartões, e um lead nunca
   * aparece nos dois lugares -- seria a mesma conversa contada duas vezes,
   * com dois operadores tentando atender.
   */
  const { leads: leadsBrutos, fila: filaBruta } = useMemo(() => separarLeads(waiting), [waiting]);

  const waitingDoCanal = useMemo(
    () => filtrarConversasDoPainel(
      filaBruta.filter((room) => passaNoCanal(room, canal)),
      { busca: buscaNaFila, origem: origemNaFila },
    ),
    [filaBruta, canal, buscaNaFila, origemNaFila],
  );

  // O lead é sempre de WhatsApp, então o filtro de canal só pode escondê-lo
  // (em "Chat do site" o cartão fica vazio, e isso é o certo).
  /*
   * Os leads do canal ANTES do filtro do cartao.
   *
   * O cartao so aparece quando ha lead, e sem esta lista ele sumiria
   * inteiro assim que a busca nao achasse nada -- a tela diria "nao ha
   * leads" quando a verdade e "o seu filtro nao achou". Sao estados
   * diferentes, e trocar um pelo outro faz o operador parar de procurar.
   */
  const leadsDoCanalSemFiltro = useMemo(
    () => leadsBrutos.filter((room) => passaNoCanal(room, canal)),
    [leadsBrutos, canal],
  );

  const leadsDoCanal = useMemo(
    () => filtrarConversasDoPainel(
      leadsDoCanalSemFiltro,
      { busca: buscaNosLeads, origem: origemNosLeads },
    ),
    [leadsDoCanalSemFiltro, buscaNosLeads, origemNosLeads],
  );

  /*
   * Quem está falando, e POR ONDE.
   *
   * Uma caixa para nome, telefone e registro -- as três respondem a MESMA
   * pergunta, e quem procura costuma ter só uma delas na mão. O detalhe que
   * mais importa está em `utils/filtrosDoPainel`: o telefone casa por DÍGITOS,
   * senão procurar "99765-4321" não acha "5519997654321".
   *
   * O "por onde" é a ORIGEM: as instâncias de WhatsApp que existirem **e o
   * chat do site** (decisão do dono do produto em 14/10).
   *
   * As origens de cada cartão saem da lista recortada só pelo CANAL -- nunca
   * pela busca nem pela própria origem. Derivar do que já está filtrado faria
   * o seletor perder as outras opções assim que uma fosse escolhida, e trocar
   * de origem exigiria passar por "Todas".
   *
   * Por cartão, e não uma lista só, porque uma marca pode estar em atendimento
   * e não ter ninguém na fila: oferecê-la na fila seria um filtro que só
   * devolve vazio.
   */
  const origensDaFila = useMemo(
    () => origensDasConversas(waiting.filter((room) => passaNoCanal(room, canal))),
    [waiting, canal],
  );

  // As origens OFERECIDAS saem dos proprios leads: oferecer uma marca que
  // nao tem lead nenhum seria um filtro que so devolve vazio.
  const origensDosLeads = useMemo(
    () => origensDasConversas(leadsBrutos.filter((room) => passaNoCanal(room, canal))),
    [leadsBrutos, canal],
  );

  const origensEmAtendimento = useMemo(
    () => origensDasConversas(
      groups.flatMap((g) => (g.chats || []).filter((chat) => passaNoCanal(chat, canal)))
    ),
    [groups, canal],
  );

  /*
   * A busca deste cartão casa o CLIENTE **ou o TÉCNICO**, porque ele é
   * agrupado por técnico -- o nome dele é o cabeçalho de cada grupo, e é uma
   * das coisas que a pessoa digita nessa caixa. Esperar que ela saiba que
   * técnico se procura em outro canto da tela é esperar demais.
   *
   * Quem casa pelo técnico vem com o grupo INTEIRO; a regra e o porquê estão
   * em `utils/filtrosDoPainel`.
   */
  const groupsDoCanal = useMemo(
    () => filtrarAtendimentosDoPainel(groups, {
      busca: buscaEmAtendimento,
      origem: origemEmAtendimento,
      passaNoCanal: (chat) => passaNoCanal(chat, canal),
    }),
    [groups, canal, buscaEmAtendimento, origemEmAtendimento],
  );

  const totalChats = groupsDoCanal.reduce((sum, g) => sum + (g.active_count || 0), 0);

  // Havia conversa, e foi o filtro de canal que a tirou da tela. Serve para
  // o vazio explicar POR QUE esta vazio.
  /*
   * "Havia conversa, e um filtro a tirou da tela" -- serve para o vazio
   * explicar o motivo em vez de dizer que não há movimento.
   *
   * Chamavam-se `canalEscondeu*` quando o canal era o único filtro capaz de
   * esvaziar a lista. Desde que cada cartão tem busca própria (14/10) isso
   * deixou de ser verdade, e um nome que afirma a causa errada é pior que um
   * nome genérico: quem lê acredita. Qual filtro foi, quem decide é a mensagem.
   */
  const filtrosEsconderamAFila = waiting.length > 0 && waitingDoCanal.length === 0;
  const filtrosEsconderamAtendimentos = groups.length > 0 && groupsDoCanal.length === 0;

  // Status de SISTEMA (users.status) — binário: 1/2 = Online, resto (0/3/4) = Offline.
  // No app, status 3 é "Inativo/offline" (o toggle do header alterna 1 <-> 3).
  // `ativo` existe para o ÍCONE escolher a forma (ver `StatusDoOperador`): a
  // cor sozinha não distinguia os dois estados para quem não separa verde de
  // cinza, nem no tema escuro.
  // O corte "online" mora em `utils/filtrosDoPainel` porque o filtro de
  // presença da tabela usa o MESMO -- se divergissem, a bolinha diria verde e
  // o filtro "Online" esconderia a linha.
  const systemStatusInfo = (s) => (statusEhOnline(s)
    ? { color: theme.palette.success.main, label: 'Online', ativo: true }
    : { color: theme.palette.text.disabled, label: 'Offline', ativo: false });
  // Status de CHAT (users.chat_status): 0 Indisponível, 1 Disponível.
  const chatStatusInfo = (c) => (c === 1
    ? { color: theme.palette.success.main, label: 'Disponível', ativo: true }
    : { color: theme.palette.text.disabled, label: 'Indisponível', ativo: false });

  // Quem pode alterar o status de chat de OUTROS operadores (Fase 5).
  const permissions = useSelector((s) => s.user?.permissions) || [];
  const canManageStatus = useMemo(() => (
    hasAdminProfile(user)
    || Boolean(user?.is_super_admin)
    || (Array.isArray(permissions) && permissions.includes('#gerenciar-status-operadores'))
  ), [permissions, user]);

  // Quem pode pegar/transferir atendimentos da fila (permissão #transferir-atendimentos).
  const canTransfer = useMemo(() => (
    hasAdminProfile(user)
    || Boolean(user?.is_super_admin)
    || (Array.isArray(permissions) && permissions.includes('#transferir-atendimentos'))
  ), [permissions, user]);

  // Quem pode encerrar conversa da FILA (ninguem pegou). A permissao existia
  // no catalogo desde sempre, sem nada que a lesse -- o botao morreu junto com
  // a pagina "Em Espera", removida na Fase 4.
  const canFinalizeWaiting = useMemo(() => (
    hasAdminProfile(user)
    || Boolean(user?.is_super_admin)
    || (Array.isArray(permissions) && permissions.includes('#finalizar-em-espera'))
  ), [permissions, user]);

  // Quem pode encerrar o atendimento EM CURSO de OUTRO operador. Permissao
  // propria: quem marcou "pode transferir" nao disse que pode encerrar.
  const canFinishOthers = useMemo(() => (
    hasAdminProfile(user)
    || Boolean(user?.is_super_admin)
    || (Array.isArray(permissions) && permissions.includes('#finalizar-atendimento-de-outro'))
  ), [permissions, user]);

  // Filtro por nome do técnico (itens 4/7) — afeta apenas a exibição dos cards de agentes e de "em atendimento".
  /*
   * ATENDIMENTOS ATIVOS AGORA (ponto 16 da Gisele, 20/10/2026).
   *
   * Sai do MESMO `agents` que o cartao de acompanhamento usa, e nao de um
   * endpoint proprio: endpoint proprio contaria com o seu proprio recorte de
   * canal e o seu proprio instante, e a diferenca apareceria na tela como "o
   * indicador diz 7, a lista mostra 5" -- defeito que esta base ja pagou
   * ("card diz 30, lista mostra 18", 06/10). As tres contas em um `useMemo`
   * so porque sao a MESMA conta agrupada de tres jeitos.
   */
  const ativosAgora = useMemo(() => ({
    total: totalAtivoAgora(agents),
    departamentos: porDepartamento(agents),
    atendentes: porAtendente(agents),
  }), [agents]);
  const [agentSearch, setAgentSearch] = useState('');
  const searchTerm = agentSearch.trim().toLowerCase();
  /*
   * Presença (pedido da Gisele, 19/10/2026): "filtrar somente quem está online
   * ali na tabela de usuários". A tabela lista TODOS os operadores do tenant,
   * e quem procura alguém para transferir uma conversa não tem o que fazer com
   * os offline.
   *
   * Dois eixos no mesmo seletor -- sistema e chat --, porque as duas colunas
   * estão lado a lado na tabela e as duas perguntas são feitas ali.
   */
  const [presencaDoAgente, setPresencaDoAgente] = useState(TODA_PRESENCA);
  /*
   * Departamento (mesmo pedido): "criar um filtro para olhar por
   * departamento". As opções saem dos agentes em tela, como o seletor de
   * instância -- oferecer um departamento sem ninguém dentro só daria um
   * filtro que devolve vazio.
   *
   * O seletor só aparece quando há MAIS DE UM departamento: com um só ele não
   * recortaria nada, e um controle que não faz nada é pior que nenhum.
   */
  /*
   * LISTA, e não um valor: "mais de um departamento" no cartão de técnicos
   * (pedido dela no bloco "Em atendimento"). Ver Suporte e Comercial juntos
   * exigia olhar um de cada vez, ou olhar todos.
   */
  const [departamentoDoAgenteFiltro, setDepartamentoDoAgenteFiltro] = useState([]);
  const departamentosDisponiveis = useMemo(() => departamentosDosAgentes(agents), [agents]);
  const filteredAgents = useMemo(
    () => filtrarAgentesDoPainel(agents, {
      busca: searchTerm,
      presenca: presencaDoAgente,
      departamento: departamentoDoAgenteFiltro,
    }),
    [agents, searchTerm, presencaDoAgente, departamentoDoAgenteFiltro],
  );
  /*
   * Departamento escolhido que deixou de existir na lista (o último operador
   * daquele grupo saiu do ar, por exemplo) sai da seleção: senão a tabela
   * ficaria vazia com um seletor mostrando uma opção que não está mais nele.
   *
   * Sai SÓ ele -- os outros marcados continuam. Zerar a seleção inteira
   * porque um departamento esvaziou desfaria uma escolha que continua
   * válida.
   */
  useEffect(() => {
    if (departamentosDisponiveis.length === 0) return;

    setDepartamentoDoAgenteFiltro((atuais) => {
      const sobreviventes = atuais.filter(
        (escolhido) => departamentosDisponiveis.some((d) => d.value === escolhido)
      );

      return sobreviventes.length === atuais.length ? atuais : sobreviventes;
    });
  }, [departamentosDisponiveis]);
  /*
   * "Buscar técnico..." recorta SÓ o cartão de agentes, desde 14/10.
   *
   * Antes recortava também "Conversas em atendimento". Agora aquele cartão tem
   * caixa própria, que casa cliente OU técnico -- e dois controles filtrando a
   * mesma lista só criam a pergunta "por que sumiu, foi qual dos dois?".
   *
   * Nada se perdeu: o que aquela caixa fazia por este cartão, a caixa dele faz
   * melhor (também acha por cliente e telefone).
   */
  const filteredGroups = groupsDoCanal;

  /*
   * Ordenar por coluna (pedido do dono do produto em 14/10).
   *
   * Dois estados separados, um por cartão, porque são listas diferentes: quem
   * ordena os agentes por "Em atend." não pediu nada sobre a fila. Um estado
   * só faria um clique mexer em duas listas.
   *
   * `null` é "ordem do servidor", que não é ausência de ordem: a fila chega na
   * ordem de chegada, e isso é informação que nenhuma coluna carrega. É para lá
   * que o terceiro clique volta.
   */
  const [ordemDosAgentes, setOrdemDosAgentes] = useState(null);
  const [ordemDaFila, setOrdemDaFila] = useState(null);
  // A terceira lista, desde que "em atendimento" virou linhas (19/10): mesmo
  // raciocínio dos dois acima -- quem ordena por técnico aqui não pediu nada
  // sobre a fila.
  const [ordemEmAtendimento, setOrdemEmAtendimento] = useState(null);

  const COLUNAS_DOS_AGENTES = useMemo(() => ({
    agente: { tipo: 'texto', valor: (a) => a.operator?.name },
    sistema: { tipo: 'numero', valor: (a) => (systemStatusInfo(a.status).ativo ? 1 : 0) },
    chat: { tipo: 'numero', valor: (a) => (chatStatusInfo(a.chat_status).ativo ? 1 : 0) },
    ativos: { tipo: 'numero', valor: (a) => a.active_count || 0 },
    atendidos: { tipo: 'numero', valor: (a) => a.attended_count },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  const COLUNAS_DA_FILA = useMemo(() => ({
    cliente: { tipo: 'texto', valor: (r) => r.client?.name },
    empresa: { tipo: 'texto', valor: (r) => r.client?.company?.business_name || r.client?.company?.name },
    // Crescente = quem chegou primeiro no topo, que é "esperando há mais
    // tempo". A coluna se chama "Espera" e é essa a leitura esperada.
    espera: { tipo: 'data', valor: (r) => r.created_at },
  }), []);

  const COLUNAS_EM_ATENDIMENTO = useMemo(() => ({
    cliente: { tipo: 'texto', valor: (c) => c.client_name },
    // O registro e TEXTO: ha codigos com zero a esquerda, e como numero
    // "0042" e "42" cairiam no mesmo lugar.
    registro: { tipo: 'texto', valor: (c) => c.client_code },
    canal: { tipo: 'texto', valor: (c) => (ehSalaDeWhatsapp(c) ? 'WhatsApp' : 'Chat') },
    produto: { tipo: 'texto', valor: (c) => marcaDaSala(c) },
    tecnico: { tipo: 'texto', valor: (c) => c.operator?.name },
    inicio: { tipo: 'data', valor: (c) => c.assigned_at || c.created_at },
    // A ultima mensagem e o que diz se a conversa esta PARADA -- ordenar por
    // ela e a pergunta "quem esta ha mais tempo sem resposta".
    ultima: { tipo: 'data', valor: (c) => c.ultima_mensagem_em || c.last_message_at },
    // Crescente = atendimento mais antigo no topo, a mesma leitura da coluna
    // "Espera" da fila.
    tempo: { tipo: 'data', valor: (c) => c.assigned_at || c.created_at },
  }), []);

  const ordenarAgentes = (chave) => setOrdemDosAgentes((atual) => proximaOrdem(atual, chave));
  const ordenarFila = (chave) => setOrdemDaFila((atual) => proximaOrdem(atual, chave));
  const ordenarEmAtendimento = (chave) => setOrdemEmAtendimento((atual) => proximaOrdem(atual, chave));

  const agentesOrdenados = useMemo(
    () => ordenarPor(filteredAgents, ordemDosAgentes, COLUNAS_DOS_AGENTES),
    [filteredAgents, ordemDosAgentes, COLUNAS_DOS_AGENTES],
  );
  const filaOrdenada = useMemo(
    () => ordenarPor(waitingDoCanal, ordemDaFila, COLUNAS_DA_FILA),
    [waitingDoCanal, ordemDaFila, COLUNAS_DA_FILA],
  );
  /*
   * "Em atendimento" deixou de ser agrupado por técnico e virou UMA LINHA POR
   * CONVERSA (Gisele, 19/10), com o técnico numa coluna. Os filtros do cartão
   * continuam trabalhando sobre os grupos (é assim que a busca por técnico
   * traz o grupo inteiro); o achatamento acontece depois deles.
   */
  const linhasEmAtendimento = useMemo(
    () => ordenarPor(conversasEmLinha(filteredGroups), ordemEmAtendimento, COLUNAS_EM_ATENDIMENTO),
    [filteredGroups, ordemEmAtendimento, COLUNAS_EM_ATENDIMENTO],
  );

  // Transferência de um atendimento da fila para um operador Disponível no chat.
  const [transfer, setTransfer] = useState({ anchor: null, room: null });
  const availableAgents = useMemo(() => agents.filter((a) => a.chat_status === 1), [agents]);
  const openTransfer = (e, room) => setTransfer({ anchor: e.currentTarget, room });
  const closeTransfer = () => setTransfer({ anchor: null, room: null });
  const handleTransfer = async (agent) => {
    const room = transfer.room;
    closeTransfer();
    if (!room) return;
    setPickingId(room.room_id);
    try {
      await http.put(`/rooms/${room.room_id}`, {
        user_id: agent.operator.id,
        invited_users: [agent.operator.id],
        client: {},
      });
      toast.success(`Atendimento transferido para ${agent.operator.name}.`);
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Não foi possível transferir o atendimento.');
    } finally {
      setPickingId(null);
    }
  };

  const handleToggleAgentChat = async (agent) => {
    if (!canManageStatus) return;
    const next = agent.chat_status === 1 ? 0 : 1;
    try {
      await http.put(`/users/${agent.operator.id}/chat-status`, { chat_status: next });
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Não foi possível alterar o status de chat.');
    }
  };

  const handleJoin = async (chat) => {
    try {
      await http.post(`/rooms/${chat.room_id}/join`, { user_id: user?.id });
    } catch (err) {
      console.error('Erro ao entrar no atendimento:', err);
    }
    setSelectedChat(null);
    setViewerChat(null);
    history.push('/chat', { roomId: chat.room_id });
  };

  /*
   * Encerrar atendimento pelo painel -- as duas formas (2º bloco da Gisele).
   *
   * ## O que já existia, e por que não funcionava
   *
   * A funcionalidade inteira estava construída e órfã: a api aceita
   * `finalize_waiting` em `PUT /rooms/{id}` desde sempre (gera ticket
   * Cancelado em vez de Pendente), a action `finalizeWaitingRoom` existe no
   * redux SEM NENHUM CHAMADOR, e a permissão `#finalizar-em-espera` está no
   * catálogo e na tela de permissões sem nada que a leia.
   *
   * O botão morreu junto com a página "Em Espera", removida na Fase 4 quando a
   * fila migrou para este painel. Ninguém o recriou aqui.
   *
   * ## As duas são diferentes de propósito
   *
   * **Da fila** (ninguém pegou): é descarte. Ticket Cancelado, e nada é
   * enviado ao cliente -- não houve atendimento, e uma despedida de um
   * atendimento que não aconteceu confundiria quem está esperando.
   *
   * **Em curso** (de outro operador): é intervenção de supervisor. Segue o
   * caminho normal de encerramento -- ticket Pendente, despedida da instância
   * e pedido de avaliação no WhatsApp --, porque o atendimento aconteceu e a
   * nota vale. O `user_id` enviado é o do operador que ATENDIA, não o do
   * supervisor: o ticket tem de ficar no nome de quem conversou.
   *
   * ## Confirmação obrigatória
   *
   * As duas fecham a sala, e fechar não tem desfazer: gera ticket e, no
   * WhatsApp, manda mensagem cobrada pela Meta. Um clique errado numa lista de
   * tempo real -- que se reordena sozinha a cada atualização -- seria fácil
   * demais.
   */
  const [encerrando, setEncerrando] = useState(null);
  const [aEncerrar, setAEncerrar] = useState(null);

  const pedirConfirmacaoDeEncerramento = (conversa, origem, operadorId) =>
    setAEncerrar({ conversa, origem, operadorId });

  const confirmarEncerramento = async () => {
    const pedido = aEncerrar;

    setAEncerrar(null);
    if (!pedido) return;

    const { conversa, origem, operadorId } = pedido;

    setEncerrando(conversa.room_id);
    try {
      if (origem === 'fila') {
        // `user_id` é o de quem está encerrando: a sala não tem operador, e a
        // api precisa de um para montar o ticket.
        await http.put(`/rooms/${conversa.room_id}`, {
          status: 0,
          user_id: user?.id,
          finalize_waiting: true,
        });
        toast.success('Atendimento da fila finalizado.');
      } else {
        await http.put(`/rooms/${conversa.room_id}`, {
          status: 0,
          user_id: operadorId,
          client: {},
        });
        toast.success('Atendimento encerrado.');
      }
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Não foi possível encerrar o atendimento.');
    } finally {
      setEncerrando(null);
    }
  };

  // Pega um chat da fila: atribui ao operador atual (PUT /rooms/{room_id}) e abre o chat.
  // Na Fase 4 o backend vai recusar se o operador não estiver Disponível no chat.
  const handlePickup = async (room) => {
    setPickingId(room.room_id);
    try {
      await http.put(`/rooms/${room.room_id}`, {
        user_id: user?.id,
        invited_users: [user?.id],
        client: {},
      });
      history.push('/chat', { roomId: room.room_id });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Não foi possível pegar o atendimento.');
    } finally {
      setPickingId(null);
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: theme.palette.background.default }}>
      {/* Cabeçalho */}
      <Box sx={{ px: 3, pt: 3, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
            Painel de Atendimentos
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Operadores, fila e atendimentos em tempo real.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Buscar técnico..."
            value={agentSearch}
            onChange={(e) => setAgentSearch(e.target.value)}
            sx={{ width: 190 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" sx={{ color: theme.palette.text.disabled }} />
                </InputAdornment>
              ),
              endAdornment: agentSearch ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setAgentSearch('')} sx={{ p: 0.25 }}>
                    <Close sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          {/*
            * O seletor de PRESENÇA fica ao lado da busca de técnico porque os
            * dois recortam o mesmo cartão (o de agentes), e não o painel.
            */}
          <Select
            size="small"
            value={presencaDoAgente}
            displayEmpty
            onChange={(e) => setPresencaDoAgente(e.target.value)}
            inputProps={{ 'aria-label': 'Presença do técnico' }}
            SelectDisplayProps={{ 'data-testid': 'filtro-de-presenca' }}
            sx={{ minWidth: 150, fontSize: '0.8125rem' }}
          >
            {PRESENCAS.map((p) => (
              <MenuItem key={p.value || 'todos'} value={p.value} sx={{ fontSize: '0.8125rem' }}>
                {p.label}
              </MenuItem>
            ))}
          </Select>
          {departamentosDisponiveis.length > 1 && (
            <Select
              size="small"
              multiple
              value={departamentoDoAgenteFiltro}
              displayEmpty
              onChange={(e) => setDepartamentoDoAgenteFiltro(e.target.value)}
              inputProps={{ 'aria-label': 'Departamento do técnico' }}
              SelectDisplayProps={{ 'data-testid': 'filtro-de-departamento' }}
              /*
               * `renderValue` é obrigatório com `multiple`: sem ele a caixa
               * fechada imprime os VALORES crus -- que aqui são ids de grupo.
               */
              renderValue={(marcados) => (
                marcados.length === 0
                  ? 'Todos os departamentos'
                  : rotulosDosDepartamentosDoPainel(marcados, departamentosDisponiveis)
              )}
              sx={{ minWidth: 170, fontSize: '0.8125rem' }}
            >
              {departamentosDisponiveis.map((d) => (
                <MenuItem key={d.value} value={d.value} sx={{ fontSize: '0.8125rem' }}>
                  <Checkbox size="small" checked={departamentoDoAgenteFiltro.indexOf(d.value) > -1} />
                  <ListItemText primary={d.label} primaryTypographyProps={{ fontSize: '0.8125rem' }} />
                </MenuItem>
              ))}
            </Select>
          )}
          {/*
            * A caixa de "cliente, telefone ou registro" SAIU daqui em 14/10 e
            * virou duas, uma no cabecalho de cada cartao de conversa. Daqui
            * ela recortava as DUAS listas ao mesmo tempo, e nada na tela
            * dizia isso: procurar um cliente na fila esvaziava "em
            * atendimento" de lambuja.
            *
            * O seletor de MARCA (MAX, SysFar) seguiu o mesmo caminho dela, e
            * pelo mesmo motivo -- cada cartao tem o seu, oferecendo so as
            * marcas que aparecem naquela lista.
            *
            * Ficam nesta barra os filtros do painel INTEIRO -- canal e
            * periodo --, e o de tecnico, que recorta o cartao de agentes.
            */}

          <Select
            size="small"
            value={canal}
            onChange={(e) => setCanal(e.target.value)}
            inputProps={{ 'aria-label': 'Canal' }}
            SelectDisplayProps={{ 'data-testid': 'filtro-de-canal' }}
            sx={{ minWidth: 150, fontSize: '0.8125rem' }}
          >
            {CANAIS.map((c) => (
              <MenuItem key={c.value} value={c.value} sx={{ fontSize: '0.8125rem' }}>{c.label}</MenuItem>
            ))}
          </Select>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Select
              size="small"
              value={period.operator}
              onChange={(e) => setPeriod({ operator: e.target.value, value: '' })}
              sx={{ minWidth: 150, fontSize: '0.8125rem' }}
            >
              {PANEL_PERIODS.map((op) => (
                <MenuItem key={op} value={op} sx={{ fontSize: '0.8125rem' }}>{DATE_PERIOD_LABELS[op]}</MenuItem>
              ))}
            </Select>
            {period.operator === 'specific_month' && (
              <TextField size="small" type="month" value={period.value}
                onChange={(e) => setPeriod((p) => ({ ...p, value: e.target.value }))}
                sx={{ width: 140 }} InputLabelProps={{ shrink: true }} />
            )}
            {(period.operator === 'specific_day' || period.operator === 'specific_week') && (
              <TextField size="small" type="date" value={period.value}
                onChange={(e) => setPeriod((p) => ({ ...p, value: e.target.value }))}
                sx={{ width: 150 }} InputLabelProps={{ shrink: true }} />
            )}
          </Box>
          <Chip icon={<HeadsetMic fontSize="small" />} label={`${agents.length} operador(es)`} variant="outlined" />
          <Chip icon={<ChatBubbleOutline fontSize="small" />} label={`${totalChats} em atendimento`} color="primary" variant="outlined" />
          <Chip icon={<AccessTime fontSize="small" />} label={`${waitingDoCanal.length} aguardando`} color="warning" variant="outlined" />
          {/* O lead não entra em "aguardando": ele não escolheu departamento,
              então não está numa fila de atendimento -- somar os dois faria o
              número do cabeçalho discordar do cartão logo abaixo. */}
          {leadsDoCanal.length > 0 && (
            <Chip icon={<WhatsApp fontSize="small" />} label={`${leadsDoCanal.length} lead(s)`} color="success" variant="outlined" />
          )}
          {lastUpdated && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: theme.palette.text.disabled }}>
              <Refresh fontSize="small" />
              <Typography variant="caption">
                {lastUpdated.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Conteúdo: agentes + fila + em atendimento */}
      <Box sx={{ px: 3, pb: 3, flex: 1, overflow: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60%' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            {/* Indicadores do chat */}
            <PanelCard title="Indicadores do chat" theme={theme}>
              {indicators.length === 0 ? (
                <Box sx={{ p: 2 }}><Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Sem dados no período.</Typography></Box>
              ) : indicators.map((ind) => {
                const changeColor = ind.change_pct > 0
                  ? theme.palette.success.main
                  : ind.change_pct < 0
                    ? theme.palette.error.main
                    : theme.palette.text.disabled;
                const changeStr = `${ind.change_pct > 0 ? '+' : ''}${Number(ind.change_pct).toLocaleString('pt-BR')}%`;
                return (
                  <Box key={ind.key} sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.1, gap: 1.5, borderBottom: `1px solid ${theme.palette.divider}`, '&:last-of-type': { borderBottom: 'none' } }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>{ind.label}</Typography>
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>{ind.description}</Typography>
                      {/* `resolved_first` e `unresolved_first` contam CHAMADOS, nao salas:
                          filtra-los por canal exigiria ir do chamado ate a sala que o
                          originou, o que muda o que os dois numeros significam. A api
                          marca quem acompanha em `segue_canal`; aqui o operador e
                          avisado, em vez de o numero contradizer os vizinhos calado. */}
                      {canal !== CANAL_TODOS && ind.segue_canal === false && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            fontWeight: 600,
                            // `warning.main` nao passa no contraste do tema claro;
                            // cada tema usa a ponta da paleta que passa na sua.
                            color: theme.palette.mode === 'dark'
                              ? theme.palette.warning.light
                              : theme.palette.warning.dark,
                          }}
                        >
                          Não acompanha o filtro de canal
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary, minWidth: 48, textAlign: 'right' }}>
                      {ind.format === 'duration'
                        ? formatDuration(ind.value)
                        : Number(ind.value).toLocaleString('pt-BR')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: changeColor, fontWeight: 700, minWidth: 64, textAlign: 'right' }}>
                      {changeStr}
                    </Typography>
                  </Box>
                );
              })}
            </PanelCard>
            </Box>

            <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            {/* Acompanhamento de agentes */}
            <PanelCard title="Acompanhamento de agentes" theme={theme}>
              {/* As cinco colunas ordenam ao clique (14/10). As larguras são as
                  mesmas das linhas abaixo -- mudar uma sem a outra desalinha a
                  tabela, e era por isso que elas já estavam fixas. */}
              <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 0.75, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <CabecalhoOrdenavel titulo="Agente" chave="agente" ordem={ordemDosAgentes} onOrdenar={ordenarAgentes} />
                <CabecalhoOrdenavel titulo="Sistema" chave="sistema" ordem={ordemDosAgentes} onOrdenar={ordenarAgentes} largura={80} alinhamento="center" />
                <CabecalhoOrdenavel titulo="Chat" chave="chat" ordem={ordemDosAgentes} onOrdenar={ordenarAgentes} largura={80} alinhamento="center" />
                <CabecalhoOrdenavel titulo="Em atend." chave="ativos" ordem={ordemDosAgentes} onOrdenar={ordenarAgentes} largura={90} alinhamento="right" dica="Atendimentos em andamento agora (tempo real)" />
                <CabecalhoOrdenavel titulo="Qtd. Atend." chave="atendidos" ordem={ordemDosAgentes} onOrdenar={ordenarAgentes} largura={110} alinhamento="right" dica="Total de atendimentos finalizados no período selecionado" />
              </Box>
              <Box sx={{ maxHeight: 340, overflowY: 'auto' }}>
              {filteredAgents.length === 0 ? (
                /* "Nenhum operador" só quando NÃO há filtro: com filtro, a
                   lista vazia é resultado da busca ou da presença, e dizer que
                   não há operador nenhum seria mentir sobre o tenant.

                   `length` no departamento, e não o valor cru: desde que ele
                   aceita vários, o campo vazio é uma LISTA vazia, que é
                   truthy -- e a tela diria "nenhum técnico encontrado", como
                   se algo estivesse filtrando, quando não há operador. */
                <Box sx={{ p: 2 }}><Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>{(searchTerm || presencaDoAgente || departamentoDoAgenteFiltro.length > 0) ? 'Nenhum técnico encontrado.' : 'Nenhum operador.'}</Typography></Box>
              ) : agentesOrdenados.map((a) => {
                const sys = systemStatusInfo(a.status);
                const cht = chatStatusInfo(a.chat_status);
                return (
                  <Box key={a.operator.id} sx={{ display: 'flex', alignItems: 'center', px: 2, py: 0.9, borderBottom: `1px solid ${theme.palette.divider}`, '&:last-of-type': { borderBottom: 'none' } }}>
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                      <Avatar src={a.operator.picture || undefined} sx={{ width: 26, height: 26, fontSize: '0.8rem', bgcolor: theme.palette.primary.main }}>
                        {a.operator.name?.charAt(0)?.toUpperCase() || '?'}
                      </Avatar>
                      <Typography variant="body2" noWrap sx={{ color: theme.palette.text.primary }}>{a.operator.name}</Typography>
                    </Box>
                    <Box sx={{ width: 80, display: 'flex', justifyContent: 'center' }}>
                      <StatusDoOperador info={sys} title={`Sistema: ${sys.label}`} />
                    </Box>
                    <Box sx={{ width: 80, display: 'flex', justifyContent: 'center' }}>
                      {/* Mesmo indicador nos dois casos; quem pode alternar
                          ganha o clique e o aviso no tooltip. Antes eram dois
                          desenhos diferentes para o mesmo estado, e a coluna
                          mudava de aparência conforme a permissão de quem
                          olhava. */}
                      <StatusDoOperador
                        info={cht}
                        title={canManageStatus ? `Chat: ${cht.label} — clique para alternar` : `Chat: ${cht.label}`}
                        onClick={canManageStatus ? () => handleToggleAgentChat(a) : undefined}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ width: 90, textAlign: 'right', fontWeight: a.active_count > 0 ? 700 : 400, color: a.active_count > 0 ? theme.palette.primary.main : theme.palette.text.secondary }}>{a.active_count || 0}</Typography>
                    <Typography variant="body2" sx={{ width: 110, textAlign: 'right', color: theme.palette.text.primary }}>{a.attended_count}</Typography>
                  </Box>
                );
              })}
              </Box>
            </PanelCard>
            </Box>
            </Box>

            {/*
              * O CARTAO "Atendimentos ativos agora" SAIU (Gisele, 23/10/2026).
              *
              * Ele repetia um numero que ja existe: o titulo de "Conversas em
              * atendimento" mostra o total, e os dois saem do MESMO `active_count`
              * -- nunca foram dois numeros, eram duas copias de um.
              *
              * Ela ofereceu tres saidas (encolher o cartao, abrir as conversas ao
              * clicar no numero, ou remover e deixar so o totalizador no cartao de
              * atendimentos) e o dono do produto escolheu a terceira, em 26/10.
              *
              * O que sai junto e a quebra por departamento e por atendente (ponto 16
              * de 20/10). `utils/atendimentosAtivosAgora` continua no repositorio,
              * com teste: se a quebra fizer falta, ela volta para dentro deste
              * cartao sem precisar ser reescrita.
              */}
            {/* As duas listas de conversa ficam LADO A LADO em tela larga.

                Antes elas ocupavam a linha inteira, uma embaixo da outra, para
                mostrar poucas conversas cada -- e era isso que empurrava a
                parte acionável do painel para fora da tela em monitor baixo.
                Lado a lado elas pedem metade da altura, e a largura que já
                sobrava passa a ser usada.

                `lg` e não `md`: no intervalo do meio a linha de cima já está
                com os dois cartões dividindo o espaço, e quebrar as de baixo
                ali deixaria quatro colunas estreitas demais para o nome do
                cliente caber. */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 2.5, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            {/*
              * LEADS (Gisele, 19/10): quem chamou no WhatsApp e não escolheu
              * departamento. O cartão só aparece quando há algum -- um cartão
              * vazio permanente na tela mais cheia do sistema seria ruído, e
              * dia nenhum garante lead.
              *
              * Fica ACIMA da fila porque é conversa que ninguém viu ainda: a
              * fila pelo menos já passou pela escolha do cliente.
              */}
            {leadsDoCanalSemFiltro.length > 0 && (
              <Box sx={{ mb: 2.5 }} data-testid="cartao-de-leads">
                <PanelCard title={`Leads (${leadsDoCanal.length})`} theme={theme} acao={<><OrigemDoCartao valor={origemNosLeads} onMudar={setOrigemNosLeads} origens={origensDosLeads} rotulo="Origem dos leads" testid="origem-dos-leads" theme={theme} /><FiltroDoCartao valor={buscaNosLeads} onMudar={setBuscaNosLeads} placeholder="Cliente, telefone ou registro..." rotulo="Filtrar os leads" theme={theme} /></>} id="leads" recolhidos={blocosRecolhidos} onAlternar={alternarBloco}>
                  <Box sx={{ px: 2, pt: 1.25, pb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                      Chamaram no WhatsApp e não escolheram departamento. Entrar na conversa assume o atendimento.
                    </Typography>
                  </Box>
                  {/* O filtro do cartão escondeu todos. Dizer "não há leads"
                      aqui mandaria o operador parar de procurar alguém que
                      está na tela, atrás de uma busca. */}
                  {leadsDoCanal.length === 0 && (
                    <Box sx={{ p: 2 }}>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        Nenhum lead para este filtro.
                      </Typography>
                    </Box>
                  )}
                  {leadsDoCanal.map((room) => {
                    const company = room.client?.company?.business_name || room.client?.company?.name;
                    return (
                      <Box key={room.room_id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.1, borderBottom: `1px solid ${theme.palette.divider}`, '&:last-of-type': { borderBottom: 'none' } }}>
                        <WhatsApp fontSize="small" sx={{ color: '#25D366' }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                            <Typography variant="body2" noWrap sx={{ fontWeight: 500, color: theme.palette.text.primary }}>{room.client?.name || 'Cliente'}</Typography>
                            {marcaDaSala(room) ? (
                              <Chip label={marcaDaSala(room)} size="small" sx={{ height: 18, fontSize: 11, fontWeight: 600, flexShrink: 0 }} />
                            ) : null}
                          </Box>
                          {/* O telefone, e não a empresa, quando não há empresa:
                              no lead ele costuma ser a única identificação que
                              existe -- a pessoa nem se apresentou ainda. */}
                          <LinhaDeIdentificacao empresa={company} telefone={telefoneLegivel(room.whatsapp_user_number)} theme={theme} />
                          <PreviaDaFila room={room} theme={theme} />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', color: theme.palette.text.disabled, minWidth: 92 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AccessTime sx={{ fontSize: 13 }} />
                            <Typography variant="caption">{elapsedSince(room.created_at)}</Typography>
                          </Box>
                          <Typography variant="caption" sx={{ fontSize: 10.5 }}>
                            {`início ${dataHoraCurta(room.created_at)}`}
                          </Typography>
                          {room.ultima_mensagem?.created_at && (
                            <Typography variant="caption" sx={{ fontSize: 10.5 }}>
                              {`última ${dataHoraCurta(room.ultima_mensagem.created_at)}`}
                            </Typography>
                          )}
                        </Box>
                        <OlhoDaConversa room={room} onVer={verConversaDaFila} />
                        {canTransfer && (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<Login />}
                            disabled={pickingId === room.room_id}
                            onClick={() => handlePickup(room)}
                            sx={{ textTransform: 'none' }}
                          >
                            {pickingId === room.room_id ? 'Entrando...' : 'Entrar'}
                          </Button>
                        )}
                      </Box>
                    );
                  })}
                </PanelCard>
              </Box>
            )}
            {/* Conversas aguardando atendimento */}
            <PanelCard title={`Conversas aguardando atendimento${waitingDoCanal.length ? ` (${waitingDoCanal.length})` : ''}`} theme={theme} acao={<><OrigemDoCartao valor={origemNaFila} onMudar={setOrigemNaFila} origens={origensDaFila} rotulo="Origem na fila" testid="origem-da-fila" theme={theme} /><FiltroDoCartao valor={buscaNaFila} onMudar={setBuscaNaFila} placeholder="Cliente, telefone ou registro..." rotulo="Filtrar a fila" theme={theme} /></>} id="fila" recolhidos={blocosRecolhidos} onAlternar={alternarBloco}>
              {/* O cabeçalho da fila é NOVO (14/10): ela nunca teve um, porque
                  nunca precisou -- a ordem era a de chegada e ponto. Ele existe
                  só para carregar a ordenação, então traz as três colunas que
                  dão para ordenar e NÃO tenta rotular o resto da linha (ícone
                  de canal, chip da marca, botões), que não ordena nada.

                  Some quando a fila está vazia: cabeçalho sobre nada é a tela
                  dizendo que há uma tabela onde não há. */}
              {filaOrdenada.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 0.75, borderBottom: `1px solid ${theme.palette.divider}` }}>
                  {/* Vão do ícone de canal, para o cabeçalho alinhar com as linhas. */}
                  <Box sx={{ width: 20, flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0, display: 'flex', gap: 1.5 }}>
                    <CabecalhoOrdenavel titulo="Cliente" chave="cliente" ordem={ordemDaFila} onOrdenar={ordenarFila} />
                    <CabecalhoOrdenavel titulo="Empresa" chave="empresa" ordem={ordemDaFila} onOrdenar={ordenarFila} />
                  </Box>
                  <CabecalhoOrdenavel
                    titulo="Espera"
                    chave="espera"
                    ordem={ordemDaFila}
                    onOrdenar={ordenarFila}
                    largura={70}
                    alinhamento="right"
                    dica="Tempo desde que a conversa entrou na fila — crescente põe quem espera há mais tempo no topo"
                  />
                </Box>
              )}
              {waitingDoCanal.length === 0 ? (
                <Box sx={{ p: 2 }}><Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>{buscaNaFila.trim() ? 'Nenhuma conversa na fila para esta busca.' : (filtrosEsconderamAFila ? 'Sem conversas aguardando neste canal.' : 'Sem conversas aguardando.')}</Typography></Box>
              ) : filaOrdenada.map((room) => {
                const company = room.client?.company?.business_name || room.client?.company?.name;
                return (
                  <Box key={room.room_id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.1, borderBottom: `1px solid ${theme.palette.divider}`, '&:last-of-type': { borderBottom: 'none' } }}>
                    {ehSalaDeWhatsapp(room)
                      ? <WhatsApp fontSize="small" sx={{ color: '#25D366' }} />
                      : <ChatBubbleOutline fontSize="small" sx={{ color: theme.palette.text.secondary }} />}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                        <Typography variant="body2" noWrap sx={{ fontWeight: 500, color: theme.palette.text.primary }}>{room.client?.name || 'Cliente'}</Typography>
                        {marcaDaSala(room) ? (
                          <Chip
                            label={marcaDaSala(room)}
                            size="small"
                            data-testid="chip-da-marca"
                            sx={{ height: 18, fontSize: 11, fontWeight: 600, flexShrink: 0 }}
                          />
                        ) : null}
                      </Box>
                      {/*
                        * Empresa e TELEFONE na mesma linha (ponto 12): o
                        * telefone é por onde se retorna, e ele estava só na
                        * ficha do cliente -- a fila mostrava apenas quem era.
                        */}
                      <LinhaDeIdentificacao empresa={company} telefone={telefoneLegivel(room.whatsapp_user_number)} theme={theme} />
                      <PreviaDaFila room={room} theme={theme} />
                    </Box>
                    {/*
                      * Três tempos, e cada um responde outra coisa: há quanto
                      * tempo espera (o que ordena a fila), quando chegou, e
                      * quando falou pela última vez. O terceiro é o que separa
                      * quem mandou "bom dia" e sumiu de quem está escrevendo
                      * agora.
                      */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', color: theme.palette.text.disabled, minWidth: 92 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AccessTime sx={{ fontSize: 13 }} />
                        <Typography variant="caption">{elapsedSince(room.created_at)}</Typography>
                      </Box>
                      <Typography variant="caption" sx={{ fontSize: 10.5 }} data-testid="inicio-da-conversa">
                        {`início ${dataHoraCurta(room.created_at)}`}
                      </Typography>
                      {room.ultima_mensagem?.created_at && (
                        <Typography variant="caption" sx={{ fontSize: 10.5 }} data-testid="ultima-mensagem-em">
                          {`última ${dataHoraCurta(room.ultima_mensagem.created_at)}`}
                        </Typography>
                      )}
                    </Box>
                    {/*
                      * O olho fica FORA do `canTransfer`: ver a conversa não é
                      * assumi-la. Quem não pode pegar continua precisando
                      * saber o que está na fila -- é a mesma informação que a
                      * prévia já dá, só completa.
                      */}
                    <OlhoDaConversa room={room} onVer={verConversaDaFila} />
                    {canTransfer && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Button size="small" variant="contained" startIcon={<Login />} disabled={pickingId === room.room_id} onClick={() => handlePickup(room)} sx={{ textTransform: 'none' }}>
                          {pickingId === room.room_id ? 'Pegando...' : 'Pegar'}
                        </Button>
                        <Tooltip title="Transferir para outro operador" arrow>
                          <span>
                            <Button size="small" variant="outlined" startIcon={<SwapHoriz />} disabled={pickingId === room.room_id} onClick={(e) => openTransfer(e, room)} sx={{ textTransform: 'none' }}>
                              Transferir
                            </Button>
                          </span>
                        </Tooltip>
                      </Box>
                    )}
                    {canFinalizeWaiting && (
                      <Tooltip title="Finalizar sem atender (gera ticket Cancelado)" arrow>
                        <span>
                          <Button
                            size="small"
                            variant="text"
                            color="error"
                            disabled={encerrando === room.room_id}
                            onClick={() => pedirConfirmacaoDeEncerramento(room, 'fila')}
                            sx={{ textTransform: 'none', flexShrink: 0 }}
                          >
                            {encerrando === room.room_id ? 'Finalizando...' : 'Finalizar'}
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                );
              })}
            </PanelCard>
            </Box>
            </Box>

            {/*
              * CONVERSAS EM ATENDIMENTO, na largura inteira (Gisele, 23/10/2026):
              * "esse card pode ocupar uma area maior da tela, facilitando o
              * acompanhamento dos atendimentos em andamento".
              *
              * Ele saiu de dentro da linha de duas colunas, que fica so com os
              * Leads e a fila. A largura e o que paga as colunas novas (registro,
              * canal, produto, inicio e ultima mensagem): espremidas em metade da
              * tela, o nome do cliente deixaria de caber.
              */}
            <Box sx={{ width: '100%', mt: 2.5 }}>
            <PanelCard title={`Conversas em atendimento${totalChats ? ` (${totalChats})` : ''}`} theme={theme} acao={<><OrigemDoCartao valor={origemEmAtendimento} onMudar={setOrigemEmAtendimento} origens={origensEmAtendimento} rotulo="Origem em atendimento" testid="origem-em-atendimento" theme={theme} /><FiltroDoCartao valor={buscaEmAtendimento} onMudar={setBuscaEmAtendimento} placeholder="Cliente, telefone ou tecnico..." rotulo="Filtrar os atendimentos" theme={theme} /></>} id="em-atendimento" recolhidos={blocosRecolhidos} onAlternar={alternarBloco}>
              {/* O vazio diz QUAL filtro esvaziou a lista. Antes falava de
                  técnico porque era o filtro do topo que recortava aqui; agora
                  a caixa é deste cartão, e ela busca cliente, telefone E
                  técnico -- "nenhum técnico encontrado" mandaria a pessoa
                  conferir a coisa errada.

                  O comentário fica ACIMA do ternário, e não dentro do ramo:
                  um comentário JSX entre o "? (" e o elemento é lido como
                  objeto, e o arquivo não compila. */}
              {/*
                * O cabeçalho das colunas (Gisele, 19/10): o cartão deixou de
                * agrupar por técnico, e o nome dele passou a ser uma COLUNA da
                * linha. Com oito técnicos atendendo, metade do cartão era
                * cabeçalho de técnico, e comparar duas conversas quaisquer --
                * quem espera mais, quem tem mensagem não lida -- exigia pular
                * de bloco em bloco.
                *
                * As larguras aqui e nas linhas abaixo são as mesmas: mudar uma
                * sem a outra desalinha a tabela.
                */}
              {linhasEmAtendimento.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 0.75, borderBottom: `1px solid ${theme.palette.divider}` }}>
                  <Box sx={{ width: 90, flexShrink: 0 }}>
                    <CabecalhoOrdenavel titulo="Canal" chave="canal" ordem={ordemEmAtendimento} onOrdenar={ordenarEmAtendimento} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <CabecalhoOrdenavel titulo="Cliente" chave="cliente" ordem={ordemEmAtendimento} onOrdenar={ordenarEmAtendimento} />
                  </Box>
                  <CabecalhoOrdenavel titulo="Registro" chave="registro" ordem={ordemEmAtendimento} onOrdenar={ordenarEmAtendimento} largura={110} dica="Registro do cliente no ERP — o CNPJ fica na linha abaixo do nome" />
                  <CabecalhoOrdenavel titulo="Produto" chave="produto" ordem={ordemEmAtendimento} onOrdenar={ordenarEmAtendimento} largura={90} dica="MAX ou SysFar, pelo número de WhatsApp que recebeu a conversa" />
                  <CabecalhoOrdenavel titulo="Técnico" chave="tecnico" ordem={ordemEmAtendimento} onOrdenar={ordenarEmAtendimento} largura={150} />
                  <CabecalhoOrdenavel
                    titulo="Tempo"
                    chave="tempo"
                    ordem={ordemEmAtendimento}
                    onOrdenar={ordenarEmAtendimento}
                    largura={60}
                    alinhamento="right"
                    dica="Tempo desde que o atendimento começou — crescente põe o mais antigo no topo"
                  />
                  {/*
                    * INÍCIO e ÚLTIMA MENSAGEM (Gisele, 23/10): "alguns
                    * atendimentos de WhatsApp podem permanecer abertos por mais
                    * de um dia -- em vez de apresentar somente o tempo da
                    * conversa, incluir data/hora de início e da última
                    * mensagem". "3 d" não distingue a conversa que acabou de
                    * receber mensagem daquela que está parada desde segunda.
                    */}
                  <CabecalhoOrdenavel titulo="Início" chave="inicio" ordem={ordemEmAtendimento} onOrdenar={ordenarEmAtendimento} largura={110} alinhamento="right" dica="Quando o atendimento começou" />
                  <CabecalhoOrdenavel titulo="Última msg" chave="ultima" ordem={ordemEmAtendimento} onOrdenar={ordenarEmAtendimento} largura={110} alinhamento="right" dica="Quando chegou a última mensagem — crescente põe a conversa mais parada no topo" />
                  {canFinishOthers && <Box sx={{ width: 86, flexShrink: 0 }} />}
                </Box>
              )}
              {linhasEmAtendimento.length === 0 ? (
                <Box sx={{ p: 2 }}><Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>{buscaEmAtendimento.trim() ? 'Nenhum atendimento para esta busca.' : (filtrosEsconderamAtendimentos ? 'Nenhum atendimento ativo neste canal.' : 'Nenhum atendimento ativo.')}</Typography></Box>
              ) : linhasEmAtendimento.map((chat) => (
                <Box
                  key={chat.room_id}
                  onClick={() => setSelectedChat({ ...chat, operatorName: chat.operator?.name })}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.1, cursor: 'pointer', borderBottom: `1px solid ${theme.palette.divider}`, '&:last-of-type': { borderBottom: 'none' }, '&:hover': { backgroundColor: theme.palette.action.hover } }}
                >
                  {/* O CANAL vira coluna, com o nome ao lado do ícone: o ícone
                      sozinho exigia saber de cor o que cada desenho quer dizer,
                      e a coluna agora tem cabeçalho e ordenação. */}
                  <Box sx={{ width: 90, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {ehSalaDeWhatsapp(chat)
                      ? <WhatsApp fontSize="small" sx={{ color: '#25D366' }} />
                      : <ChatBubbleOutline fontSize="small" sx={{ color: theme.palette.text.secondary }} />}
                    <Typography variant="caption" noWrap sx={{ color: theme.palette.text.secondary }}>
                      {ehSalaDeWhatsapp(chat) ? 'WhatsApp' : 'Chat'}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                      <Typography variant="body2" noWrap sx={{ fontWeight: 500, color: theme.palette.text.primary }}>{chat.client_name || 'Cliente'}</Typography>
                      {/* O CHIP DA MARCA SAIU daqui: ela virou a coluna
                          "Produto". Mantê-lo faria a mesma linha dizer "SysFar"
                          duas vezes -- a duplicação que a própria Gisele pediu
                          para evitar neste painel. Nos Leads e na fila o chip
                          continua: lá não há coluna. */}
                      {chat.unread > 0 && <Chip label={chat.unread} size="small" color="error" sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }} />}
                    </Box>
                    {/* O registro SAIU daqui: virou coluna própria. Repeti-lo
                        nas duas deixaria a linha dizendo a mesma coisa duas
                        vezes e roubaria largura do nome da empresa. */}
                    {chat.company_name && (
                      <Typography variant="caption" noWrap sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                        {chat.company_name}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ width: 110, flexShrink: 0 }}>
                    <Typography variant="caption" noWrap sx={{ color: theme.palette.text.secondary }}>
                      {chat.client_code || '—'}
                    </Typography>
                  </Box>
                  {/* O PRODUTO (MAX/SysFar) sai do número de WhatsApp que
                      recebeu a conversa -- a mesma `marcaDaSala` do chip. */}
                  <Box sx={{ width: 90, flexShrink: 0 }}>
                    <Typography variant="caption" noWrap sx={{ color: theme.palette.text.secondary }}>
                      {marcaDaSala(chat) || '—'}
                    </Typography>
                  </Box>
                  {/* O TÉCNICO como coluna: a mesma informação do cabeçalho de
                      antes, agora comparável linha a linha. */}
                  <Box sx={{ width: 150, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                    <Avatar src={chat.operator?.picture || undefined} sx={{ width: 22, height: 22, bgcolor: theme.palette.primary.main, fontSize: '0.7rem' }}>
                      {chat.operator?.name?.charAt(0)?.toUpperCase() || '?'}
                    </Avatar>
                    <Typography variant="caption" noWrap sx={{ color: theme.palette.text.secondary }}>
                      {chat.operator?.name || 'Sem operador'}
                    </Typography>
                  </Box>
                  <Box sx={{ width: 60, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5, color: theme.palette.text.disabled, justifyContent: 'flex-end' }}>
                    <AccessTime sx={{ fontSize: 13 }} />
                    <Typography variant="caption">{elapsedSince(chat.assigned_at || chat.created_at)}</Typography>
                  </Box>
                  <Box sx={{ width: 110, flexShrink: 0, textAlign: 'right' }}>
                    <Typography variant="caption" noWrap sx={{ color: theme.palette.text.disabled }}>
                      {dataHoraCurta(chat.assigned_at || chat.created_at)}
                    </Typography>
                  </Box>
                  <Box sx={{ width: 110, flexShrink: 0, textAlign: 'right' }}>
                    {/* Traço quando não houve mensagem nenhuma. Vazio pareceria
                        coluna que não carregou. */}
                    <Typography variant="caption" noWrap sx={{ color: theme.palette.text.disabled }}>
                      {dataHoraCurta(chat.ultima_mensagem_em || chat.last_message_at) || '—'}
                    </Typography>
                  </Box>
                  {canFinishOthers && (
                    <Box sx={{ width: 86, flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
                      <Tooltip title="Encerrar este atendimento" arrow>
                        <span>
                          <Button
                            size="small"
                            variant="text"
                            color="error"
                            disabled={encerrando === chat.room_id}
                            onClick={(e) => { e.stopPropagation(); pedirConfirmacaoDeEncerramento(chat, 'curso', chat.operator?.id); }}
                            sx={{ textTransform: 'none', flexShrink: 0 }}
                          >
                            {encerrando === chat.room_id ? 'Encerrando...' : 'Encerrar'}
                          </Button>
                        </span>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              ))}
            </PanelCard>
            </Box>
          </>
        )}
      </Box>

      {/* Menu de transferência: escolher operador disponível para receber o atendimento */}
      <Menu
        anchorEl={transfer.anchor}
        open={Boolean(transfer.anchor)}
        onClose={closeTransfer}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { maxHeight: 340, minWidth: 240 } }}
      >
        <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', fontWeight: 700, color: theme.palette.text.secondary }}>
          Transferir para
        </Typography>
        {availableAgents.length === 0 ? (
          <MenuItem disabled>Nenhum operador disponível no chat</MenuItem>
        ) : availableAgents.map((a) => (
          <MenuItem key={a.operator.id} onClick={() => handleTransfer(a)} sx={{ gap: 1.25 }}>
            <Avatar src={a.operator.picture || undefined} sx={{ width: 24, height: 24, fontSize: '0.75rem', bgcolor: theme.palette.primary.main }}>
              {a.operator.name?.charAt(0)?.toUpperCase() || '?'}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap>{a.operator.name}</Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>{a.active_count || 0} em atend.</Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>

      {/* Diálogo de escolha: visualizar ou entrar */}
      {/*
        * A confirmacao nao e cerimonia: encerrar fecha a sala e nao tem
        * desfazer -- gera ticket e, no WhatsApp, manda mensagem cobrada pela
        * Meta. Esta lista se reordena sozinha a cada atualizacao do painel,
        * entao o clique errado e facil demais sem uma parada.
        *
        * O texto diz o que VAI ACONTECER, e nao so 'tem certeza?': as duas
        * acoes tem consequencias diferentes, e quem confirma precisa saber
        * qual delas esta confirmando.
        */}
      <Dialog open={Boolean(aEncerrar)} onClose={() => setAEncerrar(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {aEncerrar?.origem === 'fila' ? 'Finalizar sem atender?' : 'Encerrar este atendimento?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {aEncerrar?.origem === 'fila' ? (
              <>
                A conversa de <strong>{aEncerrar?.conversa?.client?.name || 'Cliente'}</strong> sai da fila
                sem ter sido atendida, e o ticket fica como <strong>Cancelado</strong>.
                Nada e enviado ao cliente.
              </>
            ) : (
              <>
                O atendimento de <strong>{aEncerrar?.conversa?.client_name || 'Cliente'}</strong> sera
                encerrado e o ticket fica como <strong>Pendente</strong>, no nome de quem atendia.
                Em conversa de WhatsApp o cliente recebe a despedida e o pedido de avaliacao.
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAEncerrar(null)} color="inherit">Cancelar</Button>
          <Button onClick={confirmarEncerramento} color="error" variant="contained">
            {aEncerrar?.origem === 'fila' ? 'Finalizar' : 'Encerrar'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!selectedChat} onClose={() => setSelectedChat(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{selectedChat?.client_name || 'Atendimento'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Técnico: {selectedChat?.operatorName || '—'}
          </Typography>
          {selectedChat?.client_code && (
            <Typography variant="body2" color="text.secondary">
              Registro do cliente: {selectedChat.client_code}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            Em atendimento há {elapsedSince(selectedChat?.assigned_at || selectedChat?.created_at)}
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Como deseja abrir esta conversa?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => { setViewerChat(selectedChat); setSelectedChat(null); }}
            startIcon={<Visibility />}
            color="inherit"
            variant="outlined"
          >
            Apenas visualizar
          </Button>
          <Button
            onClick={() => handleJoin(selectedChat)}
            startIcon={<Login />}
            variant="contained"
          >
            Entrar no atendimento
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de visualização somente leitura */}
      <ChatViewerDialog
        open={!!viewerChat}
        chat={viewerChat}
        onClose={() => setViewerChat(null)}
        onJoin={handleJoin}
        // A conversa aberta a partir da fila ou dos leads é só para ver: a
        // própria linha já tem "Pegar"/"Entrar" ao lado.
        podeEntrar={!viewerChat?.somenteLeitura}
        theme={theme}
      />
    </Box>
  );
};

export default AttendancePanelPage;
